/**
 * Off-platform PayPal: customers pay the seller's PayPal directly.
 * BookAPT does not process or hold funds.
 */

const PAYPAL_HOSTS = ['paypal.com', 'paypal.me', 'www.paypal.com'];

/**
 * Web only: open a blank tab synchronously. Call this as the very first line
 * of a click handler, before any `await`, then navigate the returned window
 * once the PayPal URL is ready (see openPayPalDirectPayment's popupWindow
 * option). Browsers only honor window.open as a trusted user gesture when it
 * happens synchronously within the click.
 */
export function openBlankTabForUserGesture(): (Window & { closed?: boolean }) | null {
  if (typeof window === 'undefined' || typeof window.open !== 'function') {
    return null;
  }
  try {
    return window.open('', '_blank');
  } catch {
    return null;
  }
}

export function isPayPalDirectPaymentUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    return PAYPAL_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  } catch {
    return false;
  }
}

/**
 * Extract a PayPal.me-style username from a full PayPal URL, if the URL is
 * one of the shapes that has one: paypal.me/USER, paypal.com/paypalme/USER,
 * or paypal.com/biz/profile/USER (a business's "share my profile" link,
 * which looks like a valid PayPal URL but has no way to pre-fill an amount).
 */
function extractPayPalHandle(url: URL): string | null {
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  const segments = url.pathname.split('/').filter(Boolean);
  if (host === 'paypal.me' || host.endsWith('.paypal.me')) {
    return segments[0] || null;
  }
  if (host === 'paypal.com' || host.endsWith('.paypal.com')) {
    const meIdx = segments.findIndex((s) => s.toLowerCase() === 'paypalme');
    if (meIdx !== -1) return segments[meIdx + 1] || null;
    const profileIdx = segments.findIndex((s) => s.toLowerCase() === 'profile');
    if (profileIdx !== -1) return segments[profileIdx + 1] || null;
  }
  return null;
}

/**
 * Normalize seller input (handle, email, or full URL) to a payment URL.
 */
export function normalizePayPalDirectInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const parsed = new URL(trimmed);
      // Any recognizable PayPal URL shape is canonicalized to a paypal.me
      // link — that's the only format that supports pre-filling a payment
      // amount. Storing other shapes verbatim (e.g. a business profile page)
      // looks like a valid PayPal URL but silently drops the amount, landing
      // the customer on a blank $0.00 send-money screen instead of checkout.
      const handle = extractPayPalHandle(parsed);
      if (handle) {
        return `https://www.paypal.me/${encodeURIComponent(handle)}`;
      }
    } catch {
      /* fall through to the raw-URL validity check below */
    }
    return isPayPalDirectPaymentUrl(trimmed) ? trimmed : null;
  }

  const withoutMe = trimmed.replace(/^paypal\.me\//i, '').replace(/^@/, '');
  const handleMatch = withoutMe.match(/^([a-zA-Z0-9._-]+)$/);
  if (handleMatch) {
    return `https://www.paypal.me/${encodeURIComponent(handleMatch[1])}`;
  }

  const emailMatch = trimmed.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  if (emailMatch) {
    const business = encodeURIComponent(emailMatch[0]);
    return `https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=${business}`;
  }

  return null;
}

/** Append amount for PayPal.me links, or fixed-amount checkout links, when possible. */
export function buildPayPalPaymentUrl(
  baseUrl: string,
  amountUsd?: number
): string {
  if (amountUsd == null || !Number.isFinite(amountUsd) || amountUsd <= 0) {
    return baseUrl;
  }
  try {
    const parsed = new URL(baseUrl);

    // Classic PayPal "Buy Now" / Payment Button checkout (cgi-bin/webscr,
    // cmd=_xclick) — a real merchant checkout flow tied to a fixed amount,
    // unlike paypal.me's peer-to-peer "send money to a friend" link.
    // paypal.me links have been confirmed to sometimes drop the pre-filled
    // amount after PayPal's own login step for logged-out customers; this
    // classic button flow doesn't have that failure mode since it's what
    // it was actually designed for. Set/replace amount + currency as query
    // params rather than a path segment.
    if (
      parsed.pathname.toLowerCase().includes('cgi-bin/webscr') &&
      parsed.searchParams.get('cmd') === '_xclick'
    ) {
      parsed.searchParams.set('amount', amountUsd.toFixed(2));
      if (!parsed.searchParams.get('currency_code')) {
        parsed.searchParams.set('currency_code', 'USD');
      }
      return parsed.toString();
    }

    // Extracting the handle (rather than string-splicing the stored path)
    // targets the one paypal.me URL shape that supports a pre-filled amount,
    // and self-heals any already-stored non-paypal.me PayPal URL (e.g. a
    // business profile link saved before normalizePayPalDirectInput started
    // canonicalizing these) without requiring the seller to re-save it.
    const handle = extractPayPalHandle(parsed);
    if (handle) {
      return `https://www.paypal.me/${encodeURIComponent(handle)}/${amountUsd.toFixed(2)}`;
    }
  } catch {
    /* use base */
  }
  return baseUrl;
}

export function maskPayPalDirectUrl(url: string): string {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('paypal.me')) {
      const handle = parsed.pathname.split('/').filter(Boolean)[0];
      return handle ? `paypal.me/${handle}` : 'paypal.me';
    }
    if (parsed.pathname.toLowerCase().includes('cgi-bin/webscr') && parsed.searchParams.get('business')) {
      return parsed.searchParams.get('business') as string;
    }
    return parsed.hostname;
  } catch {
    return 'PayPal link';
  }
}
